import React, { ReactNode } from "react";
import Page, { PageProps } from "../../../core/ui/pages/Page";
import {
  Layout,
  Collapse,
  Typography,
  Spin,
  Col,
  Row,
  Space,
  Steps,
  Tooltip
} from "antd";
import { ICaseDecisionDetailsPresenter } from "../../presenter/CaseDecisionDetailsPresenter";
import OrganisationIndexTag from "../../../organisations/ui/components/OrganisationIndexTag";
import CaseDecisionSidebar from "../components/CaseDecisionSidebar";
import AttachmentTable from "../../../caseLawDocuments/ui/components/AttachmentTable";
import HeadingLabel from "../../../core/ui/components/HeadingLabel";
import { LockFilled } from "@ant-design/icons";
import Constants, { CaseDecisionStatus } from "../../common/Constants";
import CaseLawDocumentCourtCaseDescription from "../../../caseLawDocuments/ui/components/CaseLawDocumentCourtCaseDescription";
import { ContentTypes } from "../../../caseLawDocuments/common/Constants";
import CaseDecisionStatusSteps from "../components/CaseDecisionStatusSteps";
const { Title } = Typography;
const { Content, Sider } = Layout;
const { Panel } = Collapse;
export interface Props extends PageProps {}

export interface State extends ICaseDecisionDetailsPresenter {}

const renderPanel = (title: string, component: ReactNode) => {
  return (
    <Collapse defaultActiveKey={"0"} key={`details-panel-${title}`}>
      <Panel
        header={
          <span
            style={{
              fontSize: "14px",
              lineHeight: "20px",
              fontWeight: 500
            }}
          >
            {title}
          </span>
        }
        key="0"
      >
        <div style={{ marginLeft: 24 }}>{component}</div>
      </Panel>
    </Collapse>
  );
};
export default class CaseDecisionDetailsPage extends React.Component<
  Props,
  State
> {
  private subscriptionId: number = 0;

  constructor(props: any) {
    super(props);
    this.state = { ...props, current: 0 };
  }

  componentDidMount() {
    this.subscriptionId = this.state.store.subscribe((result) => {
      return this.setState(() => result);
    });
  }

  componentWillUnmount() {
    this.state.store.unsubscribe(this.subscriptionId);
  }
  render() {
    const {
      translate,
      caseDecision,
      caseDecisionTypes,
      organisations,
      attachmentTypes,
      attachmentContentTypes,
      caseDecisionAttachments,
      courtCase,
      indexHierarchyList,
      showCaseDecisionUpdatePage,
      archiveCaseDecision,
      mainAttachment,
      canEdit,
      removeCaseDecision,
      user,
      anonimisedUploadedFile,
      setAnonymisedFileValue,
      showCaseDecisionDetailsPage,
      publishCaseDecision,
      revertCaseDecisionStatus
    } = this.state;
    const { Step } = Steps;

    function renderCourtCase() {
      return (
        <div style={{ marginTop: 20 }}>
          <CaseLawDocumentCourtCaseDescription
            courtCase={courtCase}
            translate={translate}
            readonly
            showDecisions
            decisions={
              courtCase?.decisions ? [courtCase?.decisions?.[0]] : undefined
            }
          />
        </div>
      );
    }

    function renderAttachmentContent() {
      return mainAttachment?.contentType?.code ===
        ContentTypes.URL_CONTENT_TYPE ? (
        <Tooltip title={mainAttachment?.content}>
          <a
            href={mainAttachment.content}
            rel="noopener noreferrer"
            target={"_blank"}
          >
            {translate("editors.case_decisions.redirect")}
          </a>
        </Tooltip>
      ) : mainAttachment?.identificationCode !== "Y" &&
        mainAttachment?.attachmentType?.anonymisationRequired ? (
        <a
          href={`/api${Constants.endpointBasePath}/${caseDecision.id}/attachments/${mainAttachment?.id}/download`}
        >
          {translate("editors.case_decisions.download")}
        </a>
      ) : (
        <div></div>
      );
    }

    return (
      <Page {...this.props} className={"page-wrapper"}>
        <Spin spinning={this.state.loaders.caseDecisionDetailsLoader}>
          <Layout>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={10} xl={5}>
                <Title level={1} style={{ marginBottom: 30 }}>
                  {translate("editors.case_decisions.title")}
                </Title>
              </Col>
              <Col xs={24} sm={24} md={14} xl={19}>
                <Row justify="space-around" align="middle">
                  {canEdit?.(caseDecision.organisationId) ? (
                    <Col span={24}>
                      <div className={"png-steps-wrapper"}>
                        <CaseDecisionStatusSteps
                          status={caseDecision.status}
                          onClick={() => {
                            showCaseDecisionDetailsPage(
                              caseDecision.invalidatedBy
                            );
                          }}
                          translate={translate}
                        />
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </Col>
            </Row>
            <Layout>
              {canEdit(caseDecision.organisationId) &&
                caseDecision?.status !== CaseDecisionStatus.ARCHIVED && (
                  <Sider
                    className={"content-container dashboard-sider"}
                    breakpoint="xl"
                    collapsible
                    collapsedWidth="0"
                    width={"20%"}
                    style={{ height: 250 }}
                  >
                    <CaseDecisionSidebar
                      translate={translate}
                      caseDecision={caseDecision}
                      caseDecisionTypes={caseDecisionTypes}
                      organisations={organisations}
                      courtCase={courtCase}
                      onEditClick={showCaseDecisionUpdatePage}
                      onArchiveClick={archiveCaseDecision}
                      removeCaseDecision={removeCaseDecision}
                      publishCaseDecision={publishCaseDecision}
                      onUndoClick={revertCaseDecisionStatus}
                      user={user}
                    />
                  </Sider>
                )}
              <Layout className={"dashboard-content-wrapper"}>
                <Content className="site-layout-background dashboard-content-container">
                  <Row gutter={[16, 8]} className={"mx-0 mb-4"}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {renderPanel(
                        translate("editors.case_decisions.basic_data"),
                        <>
                          <Row>
                            <Col>
                              <HeadingLabel
                                text={translate("editors.case_decisions.name")}
                              />
                              <p>{caseDecision.name}</p>
                            </Col>
                          </Row>
                          {caseDecision.organisationName && (
                            <Row>
                              <Col>
                                <HeadingLabel
                                  text={translate(
                                    "editors.case_decisions.organisation_name"
                                  )}
                                />
                                <p>{caseDecision.organisationName}</p>
                              </Col>
                            </Row>
                          )}
                          {caseDecision.caseDecisionType && (
                            <Row>
                              <Col>
                                <HeadingLabel
                                  text={translate(
                                    "editors.case_decisions.case_decision_type"
                                  )}
                                />
                                <p>{caseDecision.caseDecisionType?.name}</p>
                              </Col>
                            </Row>
                          )}
                          {caseDecision.formattedDate ? (
                            <Row>
                              <Col>
                                <HeadingLabel
                                  text={translate(
                                    "editors.case_decisions.date"
                                  )}
                                />
                                <p>{caseDecision.formattedDate}</p>
                              </Col>
                            </Row>
                          ) : null}
                        </>
                      )}
                      {caseDecision.category && (
                        <Row>
                          <Col>
                            <HeadingLabel
                              text={translate(
                                "editors.case_decisions.case_decision_type"
                              )}
                            />
                            <p>{caseDecision.category}</p>
                          </Col>
                        </Row>
                      )}
                      {renderPanel(
                        translate("editors.case_decisions.court_case.details"),
                        <>
                          <Row>
                            <Col span={24}></Col>
                            <Col span={24}>{renderCourtCase()}</Col>
                          </Row>
                        </>
                      )}
                      {renderPanel(
                        translate("editors.case_decisions.main_attachment"),
                        <>
                          <Row>
                            <Col>
                              <p style={{ marginTop: "1em" }}>
                                {mainAttachment?.name}
                              </p>
                              <p style={{ marginTop: "1em" }}>
                                {mainAttachment?.description}
                              </p>
                              <p style={{ marginTop: "1em" }}>
                                {renderAttachmentContent()}
                              </p>
                            </Col>
                          </Row>
                        </>
                      )}
                      {caseDecisionAttachments.length > 0 &&
                        renderPanel(
                          translate(
                            "editors.case_decisions.additional_attachments"
                          ),
                          <>
                            <AttachmentTable
                              translate={translate}
                              attachmentTypes={attachmentTypes}
                              attachments={caseDecisionAttachments}
                              editable={false}
                              contentTypes={attachmentContentTypes}
                              canDownload={canEdit}
                              item={caseDecision}
                              organisationId={caseDecision.organisationId}
                              basePath={`${Constants.endpointBasePath}`}
                              anonimisedUploadedFile={anonimisedUploadedFile}
                              setAnonymisedFileValue={setAnonymisedFileValue}
                            />
                          </>
                        )}
                      {indexHierarchyList.length > 0 &&
                        renderPanel(
                          translate("editors.case_decisions.index_data"),
                          <>
                            {indexHierarchyList.map((indexHierarchy) => (
                              <OrganisationIndexTag
                                indexHierarchyList={indexHierarchy}
                              />
                            ))}
                          </>
                        )}
                    </Space>
                  </Row>
                </Content>
              </Layout>
            </Layout>
          </Layout>
        </Spin>
      </Page>
    );
  }
}
